import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Crosshair,
  Sparkles,
  Wrench,
  User as UserIcon
} from 'lucide-react';
import { useLang } from '../i18n';
import { useScrollDirection } from '../hooks/useScrollDirection';
import './SwipeNavigator.css';

const TABS = ['home', 'sensi', 'coach', 'actions', 'profile'];

const TAB_META = {
  home: { id: 'home', labelKey: 'nav_home', icon: Home },
  sensi: { id: 'sensi', labelKey: 'nav_sensi_short', icon: Crosshair },
  coach: { id: 'coach', labelKey: 'nav_coach', icon: Sparkles },
  actions: { id: 'actions', labelKey: 'nav_tools_short', icon: Wrench },
  profile: { id: 'profile', labelKey: 'nav_profile', icon: UserIcon }
};

const pageVariants = {
  initial: (direction) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0,
    filter: 'blur(20px)'
  }),
  in: {
    x: 0,
    opacity: 1,
    filter: 'blur(0px)'
  },
  out: (direction) => ({
    x: direction < 0 ? 1000 : -1000,
    opacity: 0,
    filter: 'blur(20px)'
  })
};

const pageTransition = {
  type: 'tween',
  ease: 'easeInOut',
  duration: 0.3
};

export default function SwipeNavigator({ activeTab, setActiveTab, children }) {
  const { t } = useLang();
  const normalizedTab = activeTab === 'hub' ? 'home' : activeTab;
  const safeTab = TABS.includes(normalizedTab)
    ? normalizedTab
    : (['calculator', 'freefire-calculator', 'mi-analysis'].includes(normalizedTab)
      ? 'actions'
      : (['training', 'history'].includes(normalizedTab) ? 'coach' : 'home'));
  const screenRef = useRef(null);
  const { scrollDirection } = useScrollDirection({
    target: screenRef,
    threshold: 8,
    topOffset: 24,
    refreshKey: safeTab,
  });

  const prevIndexRef = useRef(TABS.indexOf(safeTab));
  const currentIndex = TABS.indexOf(safeTab);

  React.useEffect(() => {
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    const behavior = reduceMotion ? 'auto' : 'smooth';

    requestAnimationFrame(() => {
      if (screenRef.current) {
        screenRef.current.scrollTo({ top: 0, left: 0, behavior });
      }
    });
  }, [activeTab, safeTab]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (screenRef.current) {
      screenRef.current.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  let direction = 1;
  if (currentIndex < prevIndexRef.current) {
    direction = -1;
  }
  prevIndexRef.current = currentIndex;

  return (
    <div className="swipe-navigator">
      <AnimatePresence initial={false} custom={direction} mode="wait">
        <motion.div
          key={safeTab}
          custom={direction}
          variants={pageVariants}
          initial="initial"
          animate="in"
          exit="out"
          transition={pageTransition}
          className="swipe-navigator__screen"
          ref={screenRef}
        >
          {children}
        </motion.div>
      </AnimatePresence>

      <div className="swipe-navigator__nav-shell" data-scroll={scrollDirection}>
        <nav className="swipe-navigator__bottom-nav" aria-label="Main navigation" role="tablist">
          {TABS.map((tabId) => {
            const tab = TAB_META[tabId];
            const Icon = tab.icon;
            const isActive = safeTab === tab.id;
            const label = t(tab.labelKey) || tab.fallback || tab.id;

            const className = [
              'swipe-navigator__tab',
              tab.isCenter ? 'swipe-navigator__tab--center' : '',
              isActive ? 'swipe-navigator__tab--active' : ''
            ].filter(Boolean).join(' ');

            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className={className}
                aria-label={label}
                aria-selected={isActive}
                aria-current={isActive ? 'page' : undefined}
                data-active={isActive ? 'true' : 'false'}
                data-tab={tab.id}
                role="tab"
              >
                <span className={tab.isCenter ? 'swipe-navigator__center-ring' : 'swipe-navigator__icon-wrap'}>
                  <Icon size={18} strokeWidth={2} />
                </span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
